import type { Tender } from '../types/tender';

// We only need specific fields from these interfaces, so we can type them loosely or exactly
export const calculateStepStatuses = (
  tender: Partial<Tender>,
  user_docs: unknown[],
  stepCompletion: Record<number, boolean>,
  sectionsCount: number,
  reviewedSectionsCount: number,
  tenderForms: { generationStatus?: string }[],
  generatedCVs: unknown[],
  reviewStepComplete: boolean
): Record<number, 'complete' | 'camel' | 'default'> => {
  const stepStatuses: Record<number, 'complete' | 'camel' | 'default'> = {};

  // 1 is always ticked (green)
  stepStatuses[1] = 'complete';

  // 2 can be camel since it is optional
  stepStatuses[2] = 'camel';

  // 3 relies on a report link being available to download
  stepStatuses[3] = tender.analysisFileLink ? 'complete' : 'default';

  // 4 camel unless at least one team is picked
  const hasTeam = tender.teamMembers && tender.teamMembers.length > 0;
  stepStatuses[4] = hasTeam ? 'complete' : 'camel';

  // 5 looks for any tick boxes in knowledge base or files
  const hasDocs =
    (user_docs || []).length > 0 ||
    Object.keys(tender?.libraryDocOverrides || {}).length > 0 ||
    !!stepCompletion[5];
  stepStatuses[5] = hasDocs ? 'complete' : 'default';

  // 6 manually ticked
  stepStatuses[6] = stepCompletion[6] ? 'complete' : 'default';

  // 7 structure is camel and then manually ticked
  stepStatuses[7] = stepCompletion[7] ? 'complete' : 'camel';

  // 8 generate looks for sections
  const hasDraft =
    !!tender.draftFileLink ||
    tender.draftResponse?.status === 'Sections Generated' ||
    tender.draftResponse?.status?.toLowerCase() === 'complete' ||
    sectionsCount > 0;
  stepStatuses[8] = hasDraft ? 'complete' : 'default';

  // 9 camel when there are sections, but goes green after all sections have a reviewed state
  if (reviewStepComplete || (sectionsCount > 0 && sectionsCount === reviewedSectionsCount)) {
    stepStatuses[9] = 'complete';
  } else if (sectionsCount > 0) {
    stepStatuses[9] = 'camel';
  } else {
    stepStatuses[9] = 'default';
  }

  // 10 looks for a docx to download
  stepStatuses[10] = tender.compiledFileLink ? 'complete' : 'default';

  // 11 is camel
  stepStatuses[11] = tender.presentationLink || tender.pdfLink ? 'complete' : 'camel';

  // 12 looks for download links for all docs in the table
  const hasForms = (tenderForms || []).length > 0;
  const allFormsGenerated =
    hasForms &&
    tenderForms.every((f: { generationStatus?: string }) => f.generationStatus === 'success');
  const someFormsGenerated =
    hasForms &&
    tenderForms.some((f: { generationStatus?: string }) => f.generationStatus === 'success');
  if (allFormsGenerated || stepCompletion[12]) {
    stepStatuses[12] = 'complete';
  } else if (someFormsGenerated || hasForms) {
    stepStatuses[12] = 'camel';
  } else {
    stepStatuses[12] = 'default';
  }

  // 13 same for cvs
  if ((generatedCVs || []).length > 0 || stepCompletion[13]) {
    stepStatuses[13] = 'complete';
  } else {
    stepStatuses[13] = 'default';
  }

  // 14 looks for a review download link
  stepStatuses[14] = tender.reviewFileLink ? 'complete' : 'default';

  return stepStatuses;
};
